import React, { useEffect, useRef, useState } from "react";
import { Box, Paper } from "@mui/material";
import { Check, ChevronDown } from "lucide-react";
import { outcomeOptions } from "./ProfileDetailPanel";

const OutcomeSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selectedLabel = outcomeOptions.find((o) => o.value === value)?.label;

  return (
    <Box sx={{ position: "relative" }} ref={containerRef}>
      <Box
        component="button"
        type="button"
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex",
          height: "40px",
          minHeight: 0,
          width: "100%",
          alignItems: "center",
          justifyContent: "space-between",
          borderRadius: "calc(0.75rem - 2px)",
          border: "1px solid hsl(140 8% 88%)",
          backgroundColor: "var(--input-bg, hsl(140 10% 95%))",
          px: "12px",
          py: "8px",
          fontSize: "14px",
          fontFamily: "Segoe UI Regular, system-ui, sans-serif",
          color: "hsl(150 30% 12%)",
          cursor: "pointer",
          boxSizing: "border-box",
          "&:focus-visible": {
            outline: "none",
            boxShadow: "0 0 0 2px hsl(140 10% 95%), 0 0 0 4px hsl(150 38% 17%)",
          },
          "&:disabled": {
            cursor: "not-allowed",
            opacity: 0.5,
          },
        }}
      >
        <Box
          component="span"
          sx={{
            overflow: "hidden",
            display: "-webkit-box",
            WebkitLineClamp: 1,
            WebkitBoxOrient: "vertical",
            color: value ? "hsl(150 30% 12%)" : "hsl(150 10% 46%)",
          }}
        >
          {value ? (selectedLabel || value) : "Log outcome\u2026"}
        </Box>
        <ChevronDown size={16} style={{ opacity: 0.5, flexShrink: 0 }} />
      </Box>
      {open && (
        <Paper
          elevation={0}
          sx={{
            position: "absolute",
            left: 0,
            bottom: "100%",
            zIndex: 50,
            mb: "4px",
            minWidth: "100%",
            borderRadius: "calc(0.75rem - 2px)",
            border: "1px solid hsl(140 8% 88%)",
            p: "4px",
            color: "hsl(150 30% 12%)",
            boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
            overflow: "hidden",
          }}
        >
          {outcomeOptions.map((option) => {
            const isSelected = value === option.value;
            return (
              <Box
                key={option.value}
                role="option"
                aria-selected={isSelected}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
                sx={{
                  position: "relative",
                  display: "flex",
                  width: "100%",
                  cursor: "default",
                  userSelect: "none",
                  alignItems: "center",
                  borderRadius: "calc(0.75rem - 4px)",
                  py: "6px",
                  pl: "32px",
                  pr: "8px",
                  fontSize: "14px",
                  fontFamily: "Segoe UI Regular, system-ui, sans-serif",
                  transition: "color 0.15s, background-color 0.15s",
                  backgroundColor: isSelected ? "hsl(80 97% 55%)" : "transparent",
                  color: isSelected ? "hsl(150 38% 12%)" : "hsl(150 30% 12%)",
                  "&:hover": {
                    backgroundColor: "hsl(80 97% 55%)",
                    color: "hsl(150 38% 12%)",
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    position: "absolute",
                    left: 8,
                    display: "flex",
                    height: 14,
                    width: 14,
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {isSelected && <Check size={16} />}
                </Box>
                <span>{option.label}</span>
              </Box>
            );
          })}
        </Paper>
      )}
    </Box>
  );
};

export default OutcomeSelect;
